import type { LinkProps } from "@mui/material";
import { Link, Stack, styled, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { pageLayout } from "@/theme/pageLayout";
import { LINKS } from "@/constants";
import { Logo } from "@trimble/modus-cmx";
import privacyChoiceIcon from "@/assets/privacy-choice-icon.png";

const FooterLink = styled((props: LinkProps) => (
  <Link target="_blank" rel="noopener noreferrer" {...props} />
))({
  color: "inherit",
  textDecoration: "none",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "22px",
  letterSpacing: "0.28px",
  whiteSpace: "nowrap",
});

const StyledLogo = styled(Logo)({
  width: 196,
  fontSize: 15,
  marginTop: 7,
  marginLeft: 12,
  marginBottom: 4,
  "svg .logo-accent": {
    fill: "var(--modus-wc-color-white)",
  },
});

type FooterLinkType = {
  label: string;
  href: string;
  showPrivacyChoiceIcon?: boolean;
};

const PrivacyChoiceIcon = () => (
  <img
    src={privacyChoiceIcon}
    alt=""
    width={36}
    height={16}
    aria-hidden="true"
  />
);

const TrimbleFooter = () => {
  const { t } = useTranslation();

  const links: FooterLinkType[] = [
    { label: t("footer.legal"), href: LINKS.LEGAL },
    { label: t("footer.terms_of_use"), href: LINKS.TERMS_OF_USE },
    { label: t("footer.privacy_center"), href: LINKS.PRIVACY_CENTER },
    { label: t("footer.privacy_notice"), href: LINKS.PRIVACY_NOTICE },
    { label: t("footer.california_notice"), href: LINKS.CALIFORNIA_NOTICE },
    {
      label: t("footer.your_privacy_choices_us"),
      href: LINKS.YOUR_PRIVACY_CHOICES_US,
      showPrivacyChoiceIcon: true,
    },
  ];

  const date = new Date();
  const utcDate = new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  );

  const year = utcDate.getFullYear();

  return (
    <Stack
      alignItems="center"
      sx={{
        minHeight: 372,
        bgcolor: "var(--modus-wc-color-trimble-gray)",
        color: "var(--modus-wc-color-white)",
        pt: 8,
        pb: 14,
      }}
    >
      <Grid
        maxWidth="xl"
        container
        spacing={2}
        justifyContent="flex-end"
        sx={{
          ...pageLayout,
          fontSize: "14px",
          lineHeight: "21px",
          fontWeight: 400,
        }}
      >
        <Grid size={{ xs: 12, md: 4, lg: 3 }}>
          <Stack gap={2}>
            <StyledLogo name="trimble" />
            <span
              style={{
                paddingLeft: 10,
                fontSize: 14,
                lineHeight: "21px",
              }}
            >
              {t("footer.copyright", { year })}
            </span>
          </Stack>
        </Grid>
        <Grid
          size={{ xs: 12, md: 8, lg: 6 }}
          px={2}
          sx={{
            fontWeight: 400,
            fontSize: "16px",
            lineHeight: "22px",
            letterSpacing: "0.32px",
          }}
        >
          {t("footer.description")}
        </Grid>
        <Grid size={{ xs: 12, md: 8, lg: 3 }}>
          <Stack gap={1.5} px={3}>
            {links.map((link) => (
              <FooterLink key={link.label} href={link.href}>
                {link.label}
                {link.showPrivacyChoiceIcon ? <PrivacyChoiceIcon /> : null}
              </FooterLink>
            ))}
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default TrimbleFooter;
